import * as React from "react";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { ReportCard } from "../ReportCard";
import { useAvailableReports } from "@/hooks/queries/useReports";
import { useCertificationReport } from "@/hooks/queries/useCertificationReport";
import { appConfig } from "@/utils/config";

interface ReportSummaryPayload {
  report_id: string;
  report_type: string;
  generated_at: string;
}

interface CertificationReportPayload {
  overall_passed: boolean;
}

/**
 * Backend v2.1 (Sprint 4.3) `GET /api/v1/reports` - lists every report
 * type actually generated for `appConfig.defaultPortfolioId`, replacing
 * Sprint 3.5's five hardcoded "unavailable" `ReportCard`s. An empty list
 * is an honest empty state (no report has been generated yet for this
 * portfolio) - never five fabricated placeholder cards. "Health Report"
 * remains sourced from Platform Certification's own read-only report
 * (`GET /certification/report`, reused via Sprint 3.2's
 * `useCertificationReport` hook, unrelated to the Reporting bounded
 * context) alongside the real Reporting entries.
 */
export function AvailableReportsSection(): React.JSX.Element {
  const reportsQuery = useAvailableReports(appConfig.defaultPortfolioId);
  const certificationQuery = useCertificationReport();

  return (
    <StatusCard title="Available Reports">
      <QueryStateBoundary
        query={reportsQuery}
        isEmpty={(data) => (data as unknown as ReportSummaryPayload[]).length === 0}
        emptyMessage="No reports have been generated for this portfolio yet."
      >
        {(payload) => {
          const reports = payload as unknown as ReportSummaryPayload[];
          return (
            <>
              {reports.map((report) => (
                <ReportCard
                  key={report.report_id}
                  name={report.report_type}
                  status="available"
                  description={`Report ID: ${report.report_id} — generated ${report.generated_at}`}
                />
              ))}
            </>
          );
        }}
      </QueryStateBoundary>
      <QueryStateBoundary query={certificationQuery}>
        {(payload) => {
          const passed = (payload as unknown as CertificationReportPayload).overall_passed;
          return (
            <ReportCard
              name="Health Report"
              status={passed ? "available" : "degraded"}
              description="Sourced from Platform Certification's read-only report (GET /certification/report)."
            />
          );
        }}
      </QueryStateBoundary>
    </StatusCard>
  );
}
